import { useContext, useEffect, useMemo, useState } from 'react';

import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    type DialogProps,
    DialogTitle,
    FormControl,
    MenuItem,
    styled
} from '@mui/material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { type RosterTableData, UserRoles } from './types';
import { CLASS_STUDENTS } from '../../config/apiPaths';
import { RosterContext } from '../../context/RosterContext';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { getUrlParams, getUrls } from '../../utils/url';
import { ControlledInput } from '../forms';
import ControlledSelect from '../forms/ControlledSelect';
import { StyledLoadingButton } from '../forms/LoadingButton';

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paper {
        width: 500px;
    }

    & .MuiDialogTitle-root {
        background-color: ${(props) => props.theme.palette.primary.main};
        color: white;
    }

    & .MuiDialogTitle-root + .MuiDialogContent-root {
        padding-top: 16px;
    }
` as typeof Dialog;

const StyledFormControl = styled(FormControl)`
    margin-bottom: 0.5rem;
`;

export type RosterEditModalProps = DialogProps & {
    data: RosterTableData | undefined;
};

type RosterForm = {
    firstName: string | undefined;
    lastName: string | undefined;
    userName: string | undefined;
    email: string | undefined;
    extUserId: string | undefined;
    role: UserRoles | undefined;
    groups: number[];
};

const RosterEditModal = (props: RosterEditModalProps) => {
    const { data, ...other } = props;

    const { setOpenEditModal, rosterData, setRosterData, selectedStudentId, myDetails, classInfo, studentGroups } =
        useContext(RosterContext);

    const userUuid = getUrlParams().user_uuid ?? window.localStorage.getItem('user_uuid');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [formValues, setFormValues] = useState<RosterForm>({
        firstName: '',
        lastName: '',
        userName: '',
        email: '',
        extUserId: '',
        role: undefined,
        groups: []
    });
    const { data: orgSettings } = useOrgSettings();
    const { api } = getUrls();
    const { control, handleSubmit } = useForm();

    const { t } = useTranslation('roster');
    const { enqueueSnackbar } = useSnackbar();

    const handleClose = () => {
        setOpenEditModal(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value
        }));
    };

    const getSelectedGroupIds = useMemo(
        () =>
            data?.student_group_names?.reduce((acc: number[], name) => {
                const groupId = studentGroups?.find((sg) => sg.label === name)?.c_student_group_id;
                if (groupId) {
                    acc.push(groupId);
                }

                return acc;
            }, []) || [],
        [data?.student_group_names, studentGroups]
    );

    const hasZoomLogin = data?.has_zoom_login;
    const isMe = myDetails?.student_id === data?.student_id;

    const onSubmit = async () => {
        if (isLoading) return;

        setIsLoading(true);

        const payload = {
            studentId: data?.student_id,
            userUuid,
            firstName: formValues.firstName,
            lastName: formValues.lastName,
            displayName: formValues.userName,
            email: formValues.email,
            extUserId: formValues.extUserId,
            role: formValues.role,
            meetingId: classInfo?.meeting_id,
            studentGroupIds: formValues.groups
        };

        try {
            const { data: updatedUser } = await axios.post(`${api}${CLASS_STUDENTS}`, payload);

            if (hasZoomLogin) {
                updatedUser.has_zoom_login = true;
            }

            const newRoster = rosterData.roster.slice();
            const removeIndex = newRoster.findIndex((record) => record.student_id === selectedStudentId);
            newRoster.splice(removeIndex, 1);
            newRoster.push(updatedUser);
            setRosterData({ loading: false, roster: newRoster });
            setOpenEditModal(false);
            setIsLoading(false);
        } catch (error: any) {
            console.error(error);
            const errorData = error?.response?.data;

            if (errorData && errorData.errorCode) {
                const errorCodeMessage = t(`class_api_error:error-${errorData.errorCode}`, {
                    displayName: errorData.errorData
                });
                enqueueSnackbar(errorCodeMessage, { variant: 'error' });
            } else {
                enqueueSnackbar(t('alert-content-9', { userName: data?.user_name }), { variant: 'error' });
            }

            setIsLoading(false);
            setOpenEditModal(false);
        }
    };

    useEffect(() => {
        if (data) {
            setFormValues({
                firstName: data.first_name ?? '',
                lastName: data.last_name ?? '',
                userName: data.user_name ?? '',
                email: data.email ?? '',
                extUserId: data.ext_user_id ?? '',
                role: (data.role as UserRoles) ?? UserRoles.Student,
                groups: getSelectedGroupIds
            });
        }
    }, [data, getSelectedGroupIds]);

    return (
        <StyledDialog {...other}>
            <DialogTitle>{t('Edit')}</DialogTitle>

            <form onSubmit={handleSubmit(onSubmit)} method="post">
                <DialogContent>
                    <StyledFormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="firstName"
                            value={formValues.firstName}
                            onChange={handleChange}
                            label={t('first-name')}
                            control={control}
                        />
                    </StyledFormControl>
                    <StyledFormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="lastName"
                            value={formValues.lastName}
                            onChange={handleChange}
                            label={t('last-name')}
                            control={control}
                        />
                    </StyledFormControl>
                    <StyledFormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="userName"
                            value={formValues.userName}
                            onChange={handleChange}
                            label={t('display-name')}
                            control={control}
                        />
                    </StyledFormControl>
                    <StyledFormControl fullWidth>
                        <ControlledSelect
                            name="role"
                            label={t('role')}
                            control={control}
                            disabled={isMe}
                            value={formValues.role}
                            onChange={handleChange}
                        >
                            <MenuItem value={UserRoles.Student}>{t('roles.student')}</MenuItem>
                            <MenuItem value={UserRoles.Assistant}>{t('roles.assistant')}</MenuItem>
                            <MenuItem value={UserRoles.Teacher}>{t('roles.teacher')}</MenuItem>
                        </ControlledSelect>
                    </StyledFormControl>
                    <StyledFormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="email"
                            value={formValues.email}
                            onChange={handleChange}
                            label={t('Email')}
                            control={control}
                        />
                    </StyledFormControl>
                    {orgSettings?.featureSettings.options.useIdentifierInRoster && <StyledFormControl fullWidth>
                        <ControlledInput
                            type="text"
                            name="extUserId"
                            value={formValues.extUserId}
                            label={t('ext-user-id')}
                            control={control}
                            helperText={t('ext-user-id-helper')}
                            disabled
                        />
                    </StyledFormControl>}
                    {formValues.role === UserRoles.Student && !!studentGroups?.length && (
                        <StyledFormControl fullWidth>
                            <ControlledSelect
                                name="groups"
                                label={t('group-names')}
                                control={control}
                                value={formValues.groups}
                                multiple
                                renderValue={(selected: any) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip
                                                key={value}
                                                label={
                                                    studentGroups!.find((sg) => sg.c_student_group_id === value)
                                                        ?.label || ''
                                                }
                                            />
                                        ))}
                                    </Box>
                                )}
                            >
                                {studentGroups.map((group) => (
                                    <MenuItem key={group.c_student_group_id} value={group.c_student_group_id}>
                                        {group.label}
                                    </MenuItem>
                                ))}
                            </ControlledSelect>
                        </StyledFormControl>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button variant={'outlined'} onClick={handleClose}>
                        {t('Cancel')}
                    </Button>

                    <StyledLoadingButton variant={'contained'} type={'submit'} loading={isLoading}>
                        {t('submit')}
                    </StyledLoadingButton>
                </DialogActions>
            </form>
        </StyledDialog>
    );
};

export default RosterEditModal;
